/* eslint-disable object-curly-spacing */
/* eslint-disable no-undef */
/* eslint-disable own-handles */
/* eslint-disable no-innerhtml-assign */
/* eslint-disable consistent-method-names */
/* eslint-disable no-jsdoc */
/* eslint-disable max-len */
/**
 * It allows to calculate a route between two locations using the TomTom's
 * [Routing API - Calculate Route](https://developer.tomtom.com/routing-api/routing-api-documentation-routing/calculate-route);
 *
 * Parameters can be passed to the constructor or provided via convenient methods that can be chained until
 * the method go performs the actual call.
 *
 * The call is asynchronous therefore the user has two options to receive the response:
 *  - Passing a callback function.
 *  - Use the {{#crossLink "Promise"}}{{/crossLink}} returned by the
 *    go method to handle the response.
 *
 * The SDK provides a {{#crossLink "Promise"}}{{/crossLink}} polyfill for browsers (IE<9)
 * without native ECMAScript 6 support.
 *
 * ### The types used in routing service
 *  - point - latitude, longitude pair (in EPSG4326 projection), with the following constraints:
 *    - Latitude values must be in the range -90..+90 inclusive.
 *    - Longitude values must be in the range -180..+180 inclusive.
 *    - Example: 52.37245,4.89406
 *
 *  - circle - A circle with a center point and a radius (in meters).
 *    - The radius must be a positive integer with the maximum value of 20050000.
 *    - Note that larger integer values will not be rejected but lowered to the maximum value.
 *    - Example: circle(52.37245,4.89406,10000)
 *
 *  - location - A point or a circle.
 *    - Example: 52.37245,4.89406  circle(52.37245,4.89406,10000)
 *
 *  - dateTime - A date and time specified in RFC-3339 format with an optional time zone offset.
 *    - Example: 1996-12-19T16:39:57  1996-12-19T16:39:57-08:00
 *
 * @class Routing
 * @module Services
 * @uses Chain
 * @uses CommonRoutingParameters
 * @constructor
 *
 * @param {Object} [options] Options to be passed to the routing call,
 *     or Array of such options objects to perform batch request.
 * @param {String} [options.key=global API key] The API key to be used.
 * @param {Function} [options.callback] Callback function to use in case of success
 * @param {Function} [options.fail=default] Callback function to use in case of failure
 * @param {Number} [options.accelerationEfficiency] Specifies the efficiency of converting chemical energy stored in
 * fuel to kinetic energy when the vehicle accelerates (i.e. KineticEnergyGained/ChemicalEnergyConsumed).
 * @param {Array} [options.allowVignette] List of 3-character ISO 3166-1 alpha-3 country codes of countries in which toll roads with vignettes are allowed.
 * @param {String} [options.alternativeType] Specifies the alternative type of the calculated alternatives compared to the reference route.
 * @param {String} [options.arriveAt] The date and time of arrival at the destination point
 * @param {Number} [options.auxiliaryPowerInkW] Specifies the amount of power consumed for sustaining auxiliary systems, in kilowatts (kW).
 * @param {Number} [options.auxiliaryPowerInLitersPerHour] Specifies the amount of fuel consumed for sustaining
 * auxiliary systems of the vehicle, in liters per hour. It can be used to specify consumption due to devices
 * and systems such as AC systems, radio, heating, etc.
 * @param {String} [options.avoid] Specifies something that the route calculation should try to avoid when
 *     determining the route.
 * @param {Array} [options.avoidAreas] A list of shapes to avoid for planning routes. <a href="#method_avoidAreas">See more</a>
 * @param {Array} [options.avoidVignette] List of 3-character ISO 3166-1 alpha-3 country codes of countries in which all toll roads with vignettes are to be avoided.
 * @param {Boolean} [options.computeBestOrder] Re-order the route waypoints to reduce the route length
 * @param {String} [options.computeTravelTimeFor] Setting this to _all_ provides additional information regards travel times.
 * @param {Array} [options.constantSpeedConsumptionInLitersPerHundredkm] Colon-delimited list of
 * CombustionConstantSpeedConsumptionPairs, e.g. array of such pairs "60,7".
 * @param {Array} [options.constantSpeedConsumptionInkWhPerHundredkm] Colon-delimited list of
 * ElectricConstantSpeedConsumptionPairs, e.g. array of such pairs "60,7".
 * @param {Number} [options.currentChargeInkWh] Specifies the current electric energy supply in kilowatt hours (kWh).
 * @param {Number} [options.currentFuelInLiters] Current supply of fuel in liters.
 * @param {Number} [options.decelerationEfficiency] Specifies the efficiency of converting kinetic energy to saved
 * (not consumed) fuel when the vehicle decelerates (i.e. ChemicalEnergySaved/KineticEnergyLost).
 * @param {String} [options.departAt] The date and time of departure from the origin point
 * @param {Number} [options.downhillEfficiency] Specifies the efficiency of converting potential energy to
 * saved (not consumed) fuel when the vehicle loses elevation (i.e. ChemicalEnergySaved/PotentialEnergyLost).
 * @param {Number} [options.fuelEnergyDensityInMJoulesPerLiter] Specifies the amount of chemical energy stored
 * in one liter of fuel in megajoules (MJ). It is used in conjunction with the Efficiency parameters for
 * conversions between saved or consumed energy and fuel.
 * @param {String} [options.hilliness] Degree of hilliness for thrilling route
 * @param {String} [options.instructionsType] If specified, guidance instructions will be returned (if
 *     available)
 * @param {String} [options.language] The language parameter determines the language of the guidance messages
 * @param {String|Array} [options.locations] Locations through which the route is calculated
 * @param {Number} [options.maxAlternatives] Number of desired alternative routes to be calculated
 * @param {Number} [options.maxChargeInkWh] Specifies the maximum electric energy supply in kilowatt hours (kWh)
 * that may be stored in the vehicle's battery.
 * @param {Number} [options.minDeviationDistance=0] At least how far **in meters** the alternative
 *     routes have to follow the reference route
 * @param {Number} [options.minDeviationTime=0] At least how far **in time (seconds)** the alternative
 *     routes have to follow the reference route
 * @param {String} [options.protocol] Protocol's type, one of "http" or "https".

 * @param {String} [options.report] Specifies which data should be reported for diagnosis purposes. Possible values:
 * * effectiveSettings reports the effective parameters or data used when calling the API. In the case of defaulted
 * parameters the default will be reflected where the parameter was not specified by the caller.
 * @param {String} [options.routeRepresentation] Specifies the representation of the set of routes provided as response
 * @param {String} [options.routeType] The type of route requested.
 * @param {String[]} [options.sectionType="travelMode"] Specifies which section types are reported in the route response. <a href="#method_sectionType">See more</a>
 * @param {Array} [options.supportingPoints] Array of supporting points used for constructing the route. <a href="#method_supportingPoints">See more</a>
 * @param {Boolean} [options.traffic] Determines if traffic data should be used to calculate the route
 * @param {String} [options.travelMode] The travel mode for the requested route
 * @param {Number} [options.uphillEfficiency] Specifies the efficiency of converting chemical energy stored in
 * fuel to potential energy when the vehicle gains elevation (i.e. PotentialEnergyGained/ChemicalEnergyConsumed).
 * @param {Number} [options.vehicleAxleWeight] The vehicle axle weight in kg.
 * @param {Boolean} [options.vehicleCommercial] True if vehicle is used for commercial purposes.
 * @param {String} [options.vehicleEngineType] The vehicle engine type.
 * @param {Number} [options.vehicleHeight] The vehicle height in m.
 * @param {Number} [options.vehicleLength] The vehicle length in meters.
 * @param {Array|String} [options.vehicleLoadType] The array of truck cargo classification.
 * @param {Number} [options.vehicleMaxSpeed] The maximal allowed vehicle speed in km/h.
 * @param {Number} [options.vehicleWeight] The vehicle weight in kg.
 * @param {Number} [options.vehicleWidth] The vehicle width in m.
 * @param {String} [options.windingness] Level of turns for thrilling route.
 *
 * @example
 * ```js
 * tomtom.routing()
 *   .locations('52.3,4.8:52.37,4.87')
 *   .go()
 *   .then(function(routeGeoJson) {
 *     console.log(routeGeoJson);
 *   });
 * ```
 */
/* eslint-enable max-len */
/* jshint nomen:false */
define([
    'rest/routing',
    'model/routing/routing',
    'model/routing/batchRouting',
    '../chain',
    '../validators',
    '../converters',
    './routingParameters'
],
function(restRouting,
    routingModel,
    batchRoutingModel,
    chain,
    validators,
    converters,
    getRoutingParameters) {
    'use strict';

    var LANGUAGE = {
        AfrikaansSouthAfrica: 'ar-ZA',
        Arabic: 'ar',
        Bulgarian: 'bg-BG',
        ChineseTaiwan: 'zh-TW',
        Czech: 'cs-CZ',
        Danish: 'da-DK',
        Dutch: 'nl-NL',
        EnglishGB: 'en-GB',
        EnglishUS: 'en-US',
        Finnish: 'fi-FI',
        French: 'fr-FR',
        German: 'de-DE',
        Greek: 'el-GR',
        Hungarian: 'hu-HU',
        Indonesian: 'id-ID',
        Italian: 'it-IT',
        Korean: 'ko-KR',
        Lithuanian: 'lt-LT',
        Malay: 'ms-MY',
        Norwegian: 'nb-NO',
        Polish: 'pl-PL',
        PortugueseBrazil: 'pt-BR',
        PortuguesePortugal: 'pt-PT',
        Russian: 'ru-RU',
        Slovak: 'sk-SK',
        Slovenian: 'sl-SI',
        Spanish: 'es-ES',
        SpanishMexico: 'es-MX',
        Swedish: 'sv-SE',
        Thai: 'th-TH',
        Turkish: 'tr-TR'
    };

    var fields = getRoutingParameters();

    var batchFields = {
        batchItems: {
            validator: validators.arrayOf(fields),
            converter: converters.arrayOf(fields)
        }
    };

    var routing = restRouting();

    function handleRouteChain(data, callback) {
        routing.calculateRoute(fields, data, function(response) {
            callback.call(this, routingModel(response));
        });
    }

    function handleBatchChain(data, callback) {
        routing.batch(fields, data, function(response) {
            callback.call(this, batchRoutingModel(response));
        });
    }

    return function(options) {
        var serviceFunction;
        options = options || {};
        options.__serviceType = 'routing';

        if (options instanceof Array) {
            serviceFunction = chain(batchFields, handleBatchChain, {
                batchItems: options
            });
        } else {
            serviceFunction = chain(fields, handleRouteChain, options);
        }
        serviceFunction.LANGUAGE = LANGUAGE;
        return serviceFunction;
    };
});



// WEBPACK FOOTER //
// ./services/routing/routing.js